import { AuthService } from "@cytiva-dxda/ps-microfrontend-utils";
import { getRelay, RelayModule } from "./loader";

type Phase = "unknown" | "anonymous" | "identified";

let phase: Phase = "unknown";
let lastUserId: string | null = null;

function clearTrackingUser(): void {
  const relay = getRelay();
  if (!relay) {
    return;
  }
  const clearFn = (relay as RelayModule & { clearIdentifiedUser?: () => void })
    .clearIdentifiedUser;
  (clearFn ?? relay.resetUser)();
}

/**
 * Keeps relay identity aligned with AuthService before route changes (and on relay ready).
 * Idempotent — skips getUserInfo when already identified with the same in-memory userId.
 *
 * @param options.userId — When the shell already has profile JSON (e.g. initial-load redirect
 *   flow), pass it to avoid a duplicate getUserInfo call.
 */
export async function syncTrackingIdentity(
  authService: AuthService,
  options?: { userId?: string }
): Promise<void> {
  if (!IS_CAT_ENABLED) {
    return;
  }

  const syncStart = performance.now();
  const relay = getRelay();
  if (!relay) {
    console.warn("[tracking] syncTrackingIdentity called but relay not loaded");
    return;
  }

  let isAuth = false;
  try {
    isAuth = await authService.isAuthenticated();
  } catch (err) {
    const syncTime = performance.now() - syncStart;
    console.error("[tracking] isAuthenticated() API call failed", {
      error: err,
      syncTime: syncTime.toFixed(2)
    });
    if (window.DD_RUM) {
      window.DD_RUM.addError(err);
    }
    phase = "anonymous";
    lastUserId = null;
    return;
  }

  if (!isAuth) {
    if (phase === "identified" || phase === "unknown") {
      console.info("[tracking] clearing user identity (logout)");
      clearTrackingUser();
    }
    phase = "anonymous";
    lastUserId = null;
    return;
  }

  const preloaded = options?.userId;
  if (preloaded !== undefined && preloaded !== "") {
    if (phase === "identified" && lastUserId === preloaded) {
      console.debug("[tracking] identity already synced, skipping (preloaded)");
      return;
    }
    const syncTime = performance.now() - syncStart;
    console.info(
      `[tracking] identifying user (preloaded) in ${syncTime.toFixed(2)}ms`
    );
    relay.identifyUser(preloaded);
    phase = "identified";
    lastUserId = preloaded;
    return;
  }

  if (phase === "identified" && lastUserId !== null) {
    console.debug("[tracking] identity already synced, skipping (cached)");
    return;
  }

  try {
    const res = await authService.getUserInfo();
    const data = await res.json();
    const id = data?.userId as string | undefined;

    const syncTime = performance.now() - syncStart;

    if (id != null && id !== "") {
      console.info(
        `[tracking] identifying user (fetched) in ${syncTime.toFixed(2)}ms`
      );
      relay.identifyUser(id);
      phase = "identified";
      lastUserId = id;
    } else {
      console.warn("[tracking] getUserInfo returned no userId", {
        syncTime: syncTime.toFixed(2)
      });
    }
  } catch (err) {
    const syncTime = performance.now() - syncStart;
    console.error("[tracking] getUserInfo() API call failed", {
      error: err,
      syncTime: syncTime.toFixed(2)
    });
    if (window.DD_RUM) {
      window.DD_RUM.addError(err);
    }

    phase = "anonymous";
    lastUserId = null;
  }
}
